/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['modalContent', 'modalOverlay'] }] */
import { Component } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import cn from 'classnames';

import { CART_PAGE_MODAL } from 'constants/amethystPageTypes';
import { HYDRA_DOUBLE_PRODUCT_IN_CART } from 'constants/hydraTests';
import {
  evProceedToCheckout,
  pvCart
} from 'events/cart';
import { cartModalViewEvent } from 'events/global';
import { isAssigned, triggerAssignment } from 'actions/ab';
import {
  fetchCartItems,
  navigateToCheckout,
  showCartModal
} from 'actions/cart';
import JanusPixel from 'components/common/JanusPixel';
import { track } from 'apis/amethyst';
import { ensureNoClass, pluralize } from 'helpers';
import { toUSD } from 'helpers/NumberFormats';
import { groupCartItemsBySizePrediction } from 'helpers/CartUtils';
import { isDesktop } from 'helpers/ClientUtils';
import { onEvent } from 'helpers/EventHelpers';
import FocusTrap from 'components/common/FocusTrap';
import { SmallLoader } from 'components/Loader';
import CartItem from 'components/cart/CartItem';
import EmptyCart from 'components/cart/EmptyCart';
import CartErrors from 'components/cart/CartErrors';
import RewardsTransparency from 'components/common/RewardsTransparency';
import marketplace from 'cfg/marketplace.json';

import modalCSS from 'styles/components/common/modal.scss';
import css from 'styles/components/cart/cartModal.scss';

const { checkout: { checkoutUrl }, cart: { cartName } } = marketplace;

export class CartModal extends Component {
  static contextTypes = {
    testId: PropTypes.func,
    router: PropTypes.object.isRequired,
    marketplace: PropTypes.object
  };

  componentDidMount() {
    const {
      cart: { cartObj: cart, isLoaded },
      fetchCartItems,
      showCartModal,
      cartModalViewEvent,
      triggerAssignment
    } = this.props;

    triggerAssignment(HYDRA_DOUBLE_PRODUCT_IN_CART);

    onEvent(window, 'keydown', this.onEscKeyDown);

    if (!isLoaded) {
      fetchCartItems().then(response => {
        track(pvCart, { ...response, isCartModal: true });
        cartModalViewEvent();
      });
    } else {
      track(pvCart, { ...cart, isCartModal: true });
      cartModalViewEvent();
    }

    if (!isDesktop(marketplace)) {
      setTimeout(() => {
        showCartModal(false);
      }, 5000);
    } else {
      this.toggleDesktopModal();
    }
  }

  componentWillUnmount() {
    ensureNoClass(document.body, 'scrollLock');
  }

  onEscKeyDown = e => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      this.onHideModal();
    }
  };

  // When cart modal is used like an actual modal on desktop, lock scroll of the body and throw aria-hidden on the root element
  // This is skipped on mobile when it is used more as a sticky header confirmation
  toggleDesktopModal = () => {
    document.documentElement.classList.toggle('scrollLock');
    const root = document.getElementById('root');
    if (root.hasAttribute('aria-hidden')) {
      root.removeAttribute('aria-hidden');
    } else {
      root.setAttribute('aria-hidden', true);
    }
  };

  onHideModal = () => {
    this.toggleDesktopModal();
    this.props.showCartModal(false);
  };

  onCheckingOut = e => {
    e.preventDefault();

    const { navigateToCheckout } = this.props;
    const { router } = this.context;

    this.onHideModal();
    track(evProceedToCheckout, { pageType: CART_PAGE_MODAL });
    navigateToCheckout(router);
  };

  makeEmptyCart = () => {
    const { isCustomer } = this.props;

    return (
      <EmptyCart onLinkClick={this.onHideModal} className={css.empty} isCustomer={isCustomer}>
        { !isCustomer &&
          <div className={css.signIn}>
            <Link to="/login">Sign In</Link>
          </div>
        }
      </EmptyCart>
    );
  };

  makeCartItems = () => {
    const { cart: { cartObj: { activeItems, savedItems } }, hydraDoubleProductInCart } = this.props;
    const { marketplace: { cart: { showFitRecommendations } } } = this.context;

    // Create three sets of cartItems, those with both correct and incorrect size predictions, and those without
    const { itemsWithFitRecommended, itemsWithFitNotRecommended, itemsWithNoRecommendationAvailable } = groupCartItemsBySizePrediction(activeItems);
    const numberOfNotRecommended = itemsWithFitNotRecommended.length;
    return (
      <>
        <div className={css.itemContainer}>
          <CartErrors isModal={true} />
          {(showFitRecommendations && hydraDoubleProductInCart)
            ?
              <>
                {Boolean(numberOfNotRecommended) &&
                  <div className={css.fitNotRecommended}>
                    <p className={css.fitCallout}>{numberOfNotRecommended === 1 ? 'This' : 'These'} {pluralize('size', numberOfNotRecommended)} might not fit you!</p>
                    <p>Our handy-dandy sizing feature suggests that {pluralize('this', numberOfNotRecommended)} {pluralize('size', numberOfNotRecommended)} might not be the best fit for you.</p>
                    {itemsWithFitNotRecommended.map(item =>
                      <CartItem
                        className={css.item}
                        item={item}
                        isModal={true}
                        key={item.cartItemId}
                      />
                    )}
                  </div>
                }
                {itemsWithFitRecommended.map(item =>
                  <CartItem
                    className={css.item}
                    item={item}
                    isModal={true}
                    isRecommendedFit={true}
                    key={item.cartItemId}
                  />
                )}
                {itemsWithNoRecommendationAvailable.map(item =>
                  <CartItem
                    className={css.item}
                    item={item}
                    isModal={true}
                    key={item.cartItemId}
                  />
                )}
              </>
            :
              <>
                {activeItems.map(item => {
                  const { cartItemId } = item;
                  return <CartItem
                    className={css.item}
                    item={item}
                    isModal={true}
                    key={cartItemId} />;
                })}
              </>
          }
          {savedItems?.map(item =>
            <CartItem
              className={css.item}
              item={item}
              isModal={true}
              isUnavailable={true}
              key={item.cartItemId} />
          )}
        </div>

        { this.makeCartFooter() }
      </>
    );
  };

  makeHeader = () => {
    const { cart: { addedStockId } } = this.props;

    return (
      <p className={css.header} id="modalDescription">
        { addedStockId
          ? <><span className={css.check} /> Added to {cartName}</>
          : `My ${cartName}`
        }
      </p>
    );
  };

  makeCartFooter = () => {
    const { cart: { cartCount, loading, cartObj: { subtotal } } } = this.props;
    const { testId } = this.context;

    return (
      <div className={css.footer}>
        <p className={css.subtotal}>
          <span data-test-id={testId('cartQuantity')}>{cartName} Subtotal ({cartCount} {pluralize('item', cartCount)})</span>
          <span data-test-id={testId('subtotal')}>{toUSD(subtotal.amount)}</span>
        </p>

        <form onSubmit={this.onCheckingOut} action={`${checkoutUrl}/initiate`} method="post">
          <Link
            to="/cart"
            onClick={this.onHideModal}
            className={css.secondaryCTA}
            data-test-id={testId('viewCart')}>View {cartName}</Link>

          <button
            type="submit"
            disabled={loading}
            className={css.primaryCTA}
            data-test-id={testId('proceedToCheckout')}>{ loading ? 'Updating...' : 'Proceed to Checkout'}</button>
        </form>
      </div>
    );
  };

  makeJanusPixel = () => {
    const { cart: { addedStockId, cartObj: cart } } = this.props;
    const addedProduct = cart.activeItems.find(item => item.stockId === addedStockId);

    if (!addedProduct) {
      return null;
    }

    const { stockId, styleId } = addedProduct;

    const queryParams = {
      teen: styleId,
      child: stockId,
      widget: 'RecordCart'
    };

    return <JanusPixel queryParams={queryParams} />;
  };

  render() {
    const { cart: { isLoaded, isModalShowing, addedStockId, cartObj: cart } } = this.props;
    const { activeItems } = cart;
    const { testId } = this.context;

    if (!isModalShowing) {
      return null;
    }

    return createPortal(
      <FocusTrap shouldFocusFirstElement={true}>
        <div
          className={css.container}
          role="dialog"
          aria-modal="true"
          aria-describedby="modalDescription"
        >
          { addedStockId && this.makeJanusPixel() }
          <div className={css.modal} data-test-id={testId('cartModal')}>
            <div className={cn(modalCSS.header, css.modalHeader)}>
              { this.makeHeader() }
              <button
                type="button"
                onClick={this.onHideModal}
                className={modalCSS.close}
                data-test-id={testId('closeModal')}
                aria-label="Close"/>
            </div>
            {
              !isLoaded
                ? <SmallLoader />
                : <>
              <RewardsTransparency isForCartModal={true} />
                {
                  !activeItems.length
                    ? this.makeEmptyCart()
                    : this.makeCartItems()
                }
              </>
            }
          </div>
        </div>
        {/* eslint-disable jsx-a11y/no-static-element-interactions */}
        {/*
          react-modal does onclick on divs
          https://github.com/reactjs/react-modal/blob/master/src/components/ModalPortal.js
        */}
        <div onClick={this.onHideModal} onKeyPress={this.onHideModal} className={css.wrapper} />
      </FocusTrap>,
      document.body
    );

  }
}

export function mapStateToProps(state) {
  const { cart, cookies } = state;
  const isCustomer = !!cookies['x-main'];

  return {
    cart,
    isCustomer,
    hydraDoubleProductInCart: isAssigned(HYDRA_DOUBLE_PRODUCT_IN_CART, 1, state)
  };
}

export default connect(mapStateToProps, {
  fetchCartItems,
  showCartModal,
  navigateToCheckout,
  cartModalViewEvent,
  triggerAssignment
})(CartModal);
